/* eslint-disable security/detect-object-injection */
import React, {Component} from 'react';
import axios from 'axios';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Switch from '@material-ui/core/Switch';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Button from '../../components/button';
import Modal from '../../components/modal';
import Banner from '../../components/banner';
import ErrorBoundary from '../../components/error-boundary';

const PERMISSION_TYPES = ['can_view', 'can_create', 'can_edit', 'can_delete'];
const PERMISSION_LABELS = {can_view: 'View', can_create: 'Create', can_edit: 'Edit', can_delete: 'Delete'};

function authHeaders() {
  return {'x-access-token': localStorage.getItem('access_token')};
}

class UserProfileAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
      loading: false,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarSeverity: 'info',

      // Tab 1 — User Roles
      allUsers: [],
      allRoles: [],
      userRoleMap: {}, // { userId: roleId | null }

      // Tab 2 — Role Permissions
      features: [],
      rolePermissions: {}, // { roleId: { featureId: { can_view, ... } } }

      // Edit / Create role dialog (shared)
      editRoleDialogOpen: false,
      editingRole: null, // null = create new, object = editing existing
      editRoleName: '',
      editRoleDisplayName: '',
      editRoleGroupType: '', // '', 'technical', 'business'

      // Delete role
      deleteRoleDialogOpen: false,
      roleToDelete: null,

      // Group master permissions
      groupPermissions: {}, // { 'technical': { featureId: { can_view, ... } }, 'business': { ... } }

      // Tab 3 — User Overrides
      selectedUserForOverride: '',
      userOverrides: {}, // { featureId: { can_view, ... } }

      // Tab 4 — Workflow Permissions
      workflowPermissions: [], // [{ role_id, role_name, can_approve, can_reject, can_rollback }]

      // UI state
      snackbarStatus: false,
      showBanner: false,
      bannerType: 'danger',
      bannerTitle: '',
      bannerMessage: '',
    };
  }

  componentDidMount() {
    this.loadInitialData();
  }

  async loadInitialData() {
    this.setState({loading: true, snackbarStatus: true, showBanner: false});
    try {
      const [rolesRes, featuresRes, usersRes, groupPermsRes, wfPermsRes] = await Promise.all([
        axios.get('/userProfileAdmin/roles', {headers: authHeaders()}),
        axios.get('/userProfileAdmin/features', {headers: authHeaders()}),
        axios.get('/admin/getUsers', {headers: authHeaders()}),
        axios.get('/userProfileAdmin/groupPermissions', {headers: authHeaders()}),
        axios.get(`${process.env.REACT_APP_API_URL}/workflow/permissions`, {headers: authHeaders()}).catch(() => ({data: {response: []}})),
      ]);

      const roles = (rolesRes.data.response && rolesRes.data.response.value) || [];
      const features = (featuresRes.data.response && featuresRes.data.response.value) || [];
      const usersRaw = (usersRes.data.response && usersRes.data.response.value) || [];

      // Build role permissions map from roles response (each role has permissions array)
      const rolePermissions = {};
      roles.forEach((role) => {
        rolePermissions[role.id] = {};
        (role.permissions || []).forEach((p) => {
          rolePermissions[role.id][p.feature_id] = {
            can_view: p.can_view,
            can_create: p.can_create,
            can_edit: p.can_edit,
            can_delete: p.can_delete,
          };
        });
      });

      // Build user-role map
      const allUsers = Array.isArray(usersRaw) ? usersRaw : [];
      const userRoleMap = {};
      await Promise.all(
          allUsers.map(async (u) => {
            try {
              const r = await axios.get(`/userProfileAdmin/users/${u.userId}/roles`, {headers: authHeaders()});
              const userRoles = (r.data.response && r.data.response.value) || [];
              userRoleMap[u.userId] = userRoles.length > 0 ? userRoles[0].id : null;
            } catch (e) {
              userRoleMap[u.userId] = null;
            }
          }),
      );

      // Process group permissions
      const groupPermsRaw = (groupPermsRes.data.response && groupPermsRes.data.response.value) || {};
      const groupPermissions = {};
      Object.keys(groupPermsRaw).forEach((groupType) => {
        groupPermissions[groupType] = {};
        (groupPermsRaw[groupType] || []).forEach((p) => {
          groupPermissions[groupType][p.feature_id] = {
            can_view: p.can_view,
            can_create: p.can_create,
            can_edit: p.can_edit,
            can_delete: p.can_delete,
          };
        });
      });

      this.setState({
        allRoles: roles,
        features,
        allUsers,
        userRoleMap,
        rolePermissions,
        groupPermissions,
        workflowPermissions: (wfPermsRes.data && wfPermsRes.data.response) || [],
        loading: false,
        snackbarStatus: false,
      });
    } catch (err) {
      this.setState({
        loading: false,
        snackbarStatus: false,
        showBanner: true,
        bannerType: 'danger',
        bannerTitle: 'Oh snap! Something went wrong!',
        bannerMessage: 'Failed to load data. Please refresh the page.',
      });
    }
  }

  showSnackbar(message, severity = 'info') {
    this.setState({snackbarOpen: true, snackbarMessage: message, snackbarSeverity: severity});
  }

  // ── Tab 1: User Roles ────────────────────────────────────────────────────────

  handleRoleToggle = async (userId, roleId) => {
    const userRoleMap = {...this.state.userRoleMap};
    const currentRole = userRoleMap[userId];
    const newRole = currentRole === roleId ? null : roleId;
    userRoleMap[userId] = newRole;
    this.setState({userRoleMap, snackbarOpen: true, snackbarMessage: 'Saving...', snackbarSeverity: 'info'});
    try {
      await axios.put(`/userProfileAdmin/users/${userId}/roles`, {roleIds: newRole ? [newRole] : []}, {headers: authHeaders()});
      this.showSnackbar('Role updated successfully', 'success');
    } catch (err) {
      this.showSnackbar('Failed to update role', 'error');
    }
  };

  // ── Tab 2: Role Permissions ──────────────────────────────────────────────────

  handleRolePermissionToggle = async (roleId, featureId, permType, checked) => {
    const rolePermissions = JSON.parse(JSON.stringify(this.state.rolePermissions));
    if (!rolePermissions[roleId]) rolePermissions[roleId] = {};
    if (!rolePermissions[roleId][featureId]) {
      rolePermissions[roleId][featureId] = {can_view: 0, can_create: 0, can_edit: 0, can_delete: 0};
    }
    rolePermissions[roleId][featureId][permType] = checked ? 1 : 0;
    this.setState({rolePermissions, snackbarOpen: true, snackbarMessage: 'Saving...', snackbarSeverity: 'info'});

    const permsForRole = Object.entries(rolePermissions[roleId]).map(([fid, p]) => ({
      featureId: parseInt(fid, 10),
      ...p,
    }));
    try {
      await axios.put(`/userProfileAdmin/roles/${roleId}/permissions`, {permissions: permsForRole}, {headers: authHeaders()});
      this.showSnackbar('Permissions updated successfully', 'success');
    } catch (err) {
      this.showSnackbar('Failed to update permissions', 'error');
    }
  };

  // ── Tab 3: User Overrides ────────────────────────────────────────────────────

  handleUserOverrideSelect = async (user) => {
    const userId = user ? user.userId : '';
    this.setState({selectedUserForOverride: userId, userOverrides: {}});
    if (!userId) return;
    try {
      const r = await axios.get(`/userProfileAdmin/users/${userId}/permissions`, {headers: authHeaders()});
      const perms = (r.data.response && r.data.response.value) || [];
      const overrides = {};
      perms.forEach((p) => {
        overrides[p.feature_id] = {can_view: p.can_view, can_create: p.can_create, can_edit: p.can_edit, can_delete: p.can_delete};
      });
      this.setState({userOverrides: overrides});
    } catch (err) {
      this.showSnackbar('Failed to load user overrides', 'error');
    }
  };

  handleUserOverrideToggle = async (featureId, permType, checked) => {
    const userOverrides = JSON.parse(JSON.stringify(this.state.userOverrides));
    if (!userOverrides[featureId]) {
      userOverrides[featureId] = {can_view: 0, can_create: 0, can_edit: 0, can_delete: 0};
    }
    userOverrides[featureId][permType] = checked ? 1 : 0;
    this.setState({userOverrides, snackbarOpen: true, snackbarMessage: 'Saving...', snackbarSeverity: 'info'});

    const permissions = Object.entries(userOverrides).map(([fid, p]) => ({
      featureId: parseInt(fid, 10),
      ...p,
    }));
    try {
      await axios.put(
          `/userProfileAdmin/users/${this.state.selectedUserForOverride}/permissions`,
          {permissions},
          {headers: authHeaders()},
      );
      this.showSnackbar('User overrides saved successfully', 'success');
    } catch (err) {
      this.showSnackbar('Failed to save user overrides', 'error');
    }
  };

  // ── Tab 4: Workflow Permissions ──────────────────────────────────────────────

  loadWorkflowPermissions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/workflow/permissions`, {
        headers: {'x-access-token': token, 'Content-Type': 'application/json'},
      });
      const json = await res.json();
      if (json.status && json.response) {
        this.setState({workflowPermissions: json.response});
      }
    } catch (e) {/* ignore */}
  };

  handleWorkflowPermissionToggle = async (roleId, field, checked) => {
    const {workflowPermissions} = this.state;
    const existing = workflowPermissions.find((p) => p.role_id === roleId) || {};
    const updated = {...existing, [field]: checked ? 1 : 0};
    const next = workflowPermissions.some((p) => p.role_id === roleId) ?
      workflowPermissions.map((p) => p.role_id === roleId ? {...p, [field]: checked ? 1 : 0} : p) :
      [...workflowPermissions, {role_id: roleId, [field]: checked ? 1 : 0}];
    this.setState({workflowPermissions: next});
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${process.env.REACT_APP_API_URL}/workflow/permissions/${roleId}`, {
        method: 'PUT',
        headers: {'x-access-token': token, 'Content-Type': 'application/json'},
        body: JSON.stringify({
          canApprove: updated.can_approve || 0,
          canReject: updated.can_reject || 0,
          canRollback: updated.can_rollback || 0,
        }),
      });
    } catch (e) {/* ignore */}
  };

  // ── Tab 2: Create / Edit / Delete Role ──────────────────────────────────────

  handleOpenCreateRole = () => {
    this.setState({
      editRoleDialogOpen: true,
      editingRole: null,
      editRoleName: '',
      editRoleDisplayName: '',
      editRoleGroupType: '',
    });
  };

  handleOpenEditRole = (role) => {
    this.setState({
      editRoleDialogOpen: true,
      editingRole: role,
      editRoleName: role.role_name || '',
      editRoleDisplayName: role.display_name || '',
      editRoleGroupType: role.group_type || '',
    });
  };

  handleCloseEditRole = () => {
    this.setState({editRoleDialogOpen: false, editingRole: null});
  };

  handleSaveEditRole = async () => {
    const {editingRole, editRoleName, editRoleDisplayName, editRoleGroupType} = this.state;
    const data = {roleName: editRoleName.trim(), displayName: editRoleDisplayName.trim(), groupType: editRoleGroupType || null};
    try {
      if (editingRole) {
        await axios.put(`/userProfileAdmin/roles/${editingRole.id}`, data, {headers: authHeaders()});
        this.showSnackbar('Role updated');
      } else {
        await axios.post('/userProfileAdmin/roles', data, {headers: authHeaders()});
        this.showSnackbar('Role created successfully');
      }
      this.handleCloseEditRole();
      this.loadInitialData();
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to save role';
      this.showSnackbar(msg);
    }
  };

  handleDeleteRoleClick = (role) => {
    this.setState({deleteRoleDialogOpen: true, roleToDelete: role});
  };

  handleConfirmDeleteRole = async () => {
    const {roleToDelete} = this.state;
    if (!roleToDelete) return;
    try {
      await axios.delete(`/userProfileAdmin/roles/${roleToDelete.id}`, {headers: authHeaders()});
      this.setState({deleteRoleDialogOpen: false, roleToDelete: null});
      this.loadInitialData();
      this.showSnackbar('Role deleted successfully');
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to delete role';
      this.showSnackbar(msg);
      this.setState({deleteRoleDialogOpen: false, roleToDelete: null});
    }
  };

  // ── Group master permissions ──────────────────────────────────────────────

  handleGroupPermissionToggle = async (groupType, featureId, permType, checked) => {
    const groupPermissions = JSON.parse(JSON.stringify(this.state.groupPermissions));
    if (!groupPermissions[groupType]) groupPermissions[groupType] = {};
    if (!groupPermissions[groupType][featureId]) {
      groupPermissions[groupType][featureId] = {can_view: 0, can_create: 0, can_edit: 0, can_delete: 0};
    }
    groupPermissions[groupType][featureId][permType] = checked ? 1 : 0;
    this.setState({groupPermissions, snackbarOpen: true, snackbarMessage: 'Saving...', snackbarSeverity: 'info'});

    const permsForGroup = Object.entries(groupPermissions[groupType]).map(([fid, p]) => ({
      featureId: parseInt(fid, 10),
      ...p,
    }));
    try {
      await axios.put(`/userProfileAdmin/groupPermissions/${groupType}`, {permissions: permsForGroup}, {headers: authHeaders()});
      this.showSnackbar('Group permissions updated successfully', 'success');
    } catch (err) {
      this.showSnackbar('Failed to update group permissions', 'error');
    }
  };

  goBack = () => {
    if (this.props.parentNavBar) {
      this.props.parentNavBar(0);
    } else if (this.props.history) {
      this.props.history.push('/settings');
    }
  };

  dismissBanner = () => {
    this.setState({showBanner: false});
  };

  render() {
    const {activeTab, loading, allUsers, allRoles, features, userRoleMap, rolePermissions,
      selectedUserForOverride, userOverrides, snackbarOpen, snackbarMessage, snackbarSeverity,
      editRoleDialogOpen, editingRole, editRoleName, editRoleDisplayName, editRoleGroupType,
      deleteRoleDialogOpen, roleToDelete, groupPermissions, workflowPermissions} = this.state;

    const technicalRoles = allRoles.filter((r) => r.group_type === 'technical');
    const businessRoles = allRoles.filter((r) => r.group_type === 'business');
    const ungroupedRoles = allRoles.filter((r) => !r.group_type);

    return (
      <div className='app-content-container'>

        <Snackbar
          open={loading || snackbarOpen}
          autoHideDuration={loading ? null : 3000}
          onClose={loading ? null : () => this.setState({snackbarOpen: false})}
          className="snack-bar"
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        >
          <Alert
            onClose={() => this.setState({snackbarOpen: false})}
            severity={loading ? 'info' : snackbarSeverity}
            variant="filled"
          >
            {loading ? 'Please wait...' : snackbarMessage}
          </Alert>
        </Snackbar>

        <Banner
          type={this.state.bannerType}
          hasAlerts={this.state.showBanner}
          header={this.state.bannerTitle}
          body={this.state.bannerMessage}
          dismissBanner={this.dismissBanner}
        />

        <div className="page-content select-container in">
          <h2>User Profile Administration</h2>

          <Tabs className='recon-tabs' value={activeTab} onChange={(e, v) => this.setState({activeTab: v})} indicatorColor="primary" textColor="primary">
            <Tab label="User Roles" />
            <Tab label="Role Permissions" />
            <Tab label="User Overrides" />
            <Tab label="Workflow Permissions" />
          </Tabs>

          {/* ── Tab 1: User Roles ── */}
          {activeTab === 0 && (
            <div className='tab-content'>
              <div className='cz-container'>
                <ErrorBoundary>
                  <div className="rounded-card profile-admin-table-wrapper">
                    <Table className='data-table'>
                      <TableHead>
                        <TableRow>
                          <TableCell><b>User</b></TableCell>
                          <TableCell><b>Email</b></TableCell>
                          {allRoles.map((role) => (
                            <TableCell key={role.id} align="center"><b>{role.display_name}</b></TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allUsers.map((user) => (
                          <TableRow key={user.userId} hover>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            {allRoles.map((role) => {
                              const assigned = userRoleMap[user.userId] === role.id;
                              return (
                                <TableCell key={role.id} align="center">
                                  <Radio
                                    checked={assigned}
                                    color="primary"
                                    onClick={() => this.handleRoleToggle(user.userId, role.id)}
                                    inputProps={{'aria-label': `${role.display_name} for ${user.name}`}}
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                        {allUsers.length === 0 && (
                          <TableRow><TableCell colSpan={2 + allRoles.length} align="center">No users found</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </ErrorBoundary>
              </div>
            </div>
          )}

          {/* ── Tab 2: Role Permissions ── */}
          {activeTab === 1 && (
            <div className='tab-content'>
              <div className='cz-container'>
                <div className="profile-admin-add-btn">
                  <Button label="Add New Role" className="btn btn-action" onClick={this.handleOpenCreateRole} />
                </div>

                {/* Technical User Group */}
                {technicalRoles.length > 0 && (
                  <ErrorBoundary>
                    <div>
                      <h3 className="profile-admin-section-title">
                        Technical User Group
                      </h3>

                      {/* Master Permissions for Technical */}
                      <div className="rounded-card ligh-gray">
                        <h4 className="master-perms-label">
                          Master Permissions <span>(applies to all Technical roles)</span>
                        </h4>
                        <Table className='data-table' size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><b>Feature</b></TableCell>
                              {PERMISSION_TYPES.map((pt) => (
                                <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {features.map((feature) => {
                              const perms = (groupPermissions['technical'] && groupPermissions['technical'][feature.id]) || {};
                              return (
                                <TableRow key={feature.id} hover>
                                  <TableCell>{feature.display_name}</TableCell>
                                  {PERMISSION_TYPES.map((pt) => (
                                    <TableCell key={pt} align="center">
                                      <Switch
                                        checked={Boolean(perms[pt])}
                                        color="primary"
                                        onChange={(e) => this.handleGroupPermissionToggle('technical', feature.id, pt, e.target.checked)}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Individual Technical Roles */}
                      {technicalRoles.map((role) => (
                        <div key={role.id} className="rounded-card">
                          <div className="role-card-header">
                            <h4>{role.display_name}</h4>
                            <div className="role-card-actions">
                              {role.role_name !== 'super_user' && (
                                <>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleOpenEditRole(role)}>
                                    <i className="material-icons">edit</i>
                                  </IconButton>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleDeleteRoleClick(role)}>
                                    <i className="material-icons">delete</i>
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </div>
                          <Table className='data-table'>
                            <TableHead>
                              <TableRow>
                                <TableCell><b>Feature</b></TableCell>
                                {PERMISSION_TYPES.map((pt) => (
                                  <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {features.map((feature) => {
                                const perms = (rolePermissions[role.id] && rolePermissions[role.id][feature.id]) || {};
                                return (
                                  <TableRow key={feature.id} hover>
                                    <TableCell>{feature.display_name}</TableCell>
                                    {PERMISSION_TYPES.map((pt) => (
                                      <TableCell key={pt} align="center">
                                        <Switch
                                          checked={Boolean(perms[pt])}
                                          color="primary"
                                          onChange={(e) => this.handleRolePermissionToggle(role.id, feature.id, pt, e.target.checked)}
                                        />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </ErrorBoundary>
                )}

                {/* Business User Group */}
                {businessRoles.length > 0 && (
                  <ErrorBoundary>
                    <div>
                      <h3 className="profile-admin-section-title">
                        Business User Group
                      </h3>

                      {/* Master Permissions for Business */}
                      <div className="rounded-card ligh-gray">
                        <h4 className="master-perms-label">
                          Master Permissions <span>(applies to all Business roles)</span>
                        </h4>
                        <Table className='data-table' size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><b>Feature</b></TableCell>
                              {PERMISSION_TYPES.map((pt) => (
                                <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {features.map((feature) => {
                              const perms = (groupPermissions['business'] && groupPermissions['business'][feature.id]) || {};
                              return (
                                <TableRow key={feature.id} hover>
                                  <TableCell>{feature.display_name}</TableCell>
                                  {PERMISSION_TYPES.map((pt) => (
                                    <TableCell key={pt} align="center">
                                      <Switch
                                        checked={Boolean(perms[pt])}
                                        color="primary"
                                        onChange={(e) => this.handleGroupPermissionToggle('business', feature.id, pt, e.target.checked)}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Individual Business Roles */}
                      {businessRoles.map((role) => (
                        <div key={role.id} className="rounded-card">
                          <div className="role-card-header">
                            <h4>{role.display_name}</h4>
                            <div className="role-card-actions">
                              {role.role_name !== 'super_user' && (
                                <>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleOpenEditRole(role)}>
                                    <i className="material-icons">edit</i>
                                  </IconButton>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleDeleteRoleClick(role)}>
                                    <i className="material-icons">delete</i>
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </div>
                          <Table className='data-table'>
                            <TableHead>
                              <TableRow>
                                <TableCell><b>Feature</b></TableCell>
                                {PERMISSION_TYPES.map((pt) => (
                                  <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {features.map((feature) => {
                                const perms = (rolePermissions[role.id] && rolePermissions[role.id][feature.id]) || {};
                                return (
                                  <TableRow key={feature.id} hover>
                                    <TableCell>{feature.display_name}</TableCell>
                                    {PERMISSION_TYPES.map((pt) => (
                                      <TableCell key={pt} align="center">
                                        <Switch
                                          checked={Boolean(perms[pt])}
                                          color="primary"
                                          onChange={(e) => this.handleRolePermissionToggle(role.id, feature.id, pt, e.target.checked)}
                                        />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </ErrorBoundary>
                )}

                {/* Ungrouped / Other Roles */}
                {ungroupedRoles.length > 0 && (
                  <ErrorBoundary>
                    <div>
                      <h3 className="profile-admin-other-title">
                        Other Roles
                      </h3>
                      {ungroupedRoles.map((role) => (
                        <div key={role.id} className="rounded-card">
                          <div className="role-card-header">
                            <h4>{role.display_name}</h4>
                            <div className="role-card-actions">
                              {role.role_name !== 'super_user' && (
                                <>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleOpenEditRole(role)}>
                                    <i className="material-icons">edit</i>
                                  </IconButton>
                                  <IconButton size="small" className="role-action-btn" onClick={() => this.handleDeleteRoleClick(role)}>
                                    <i className="material-icons">delete</i>
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </div>
                          <Table className='data-table'>
                            <TableHead>
                              <TableRow>
                                <TableCell><b>Feature</b></TableCell>
                                {PERMISSION_TYPES.map((pt) => (
                                  <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {features.map((feature) => {
                                const perms = (rolePermissions[role.id] && rolePermissions[role.id][feature.id]) || {};
                                return (
                                  <TableRow key={feature.id} hover>
                                    <TableCell>{feature.display_name}</TableCell>
                                    {PERMISSION_TYPES.map((pt) => (
                                      <TableCell key={pt} align="center">
                                        <Switch
                                          checked={Boolean(perms[pt])}
                                          color="primary"
                                          onChange={(e) => this.handleRolePermissionToggle(role.id, feature.id, pt, e.target.checked)}
                                        />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </ErrorBoundary>
                )}
              </div>
            </div>
          )}

          {/* ── Tab 3: User Overrides ── */}
          {activeTab === 2 && (
            <div className='tab-content'>
              <div className='cz-container'>
                <ErrorBoundary>
                  <div className="rounded-card">
                    <Autocomplete
                      options={allUsers}
                      getOptionLabel={(user) => `${user.name} (${user.email})`}
                      value={allUsers.find((u) => u.userId === selectedUserForOverride) || null}
                      onChange={(event, user) => this.handleUserOverrideSelect(user)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select a user"
                          variant="standard"
                          placeholder="Search by name or email..."
                        />
                      )}
                      className="profile-admin-select"
                    />

                    {selectedUserForOverride && (
                      <Table className='data-table'>
                        <TableHead>
                          <TableRow>
                            <TableCell><b>Feature</b></TableCell>
                            {PERMISSION_TYPES.map((pt) => (
                              <TableCell key={pt} align="center"><b>{PERMISSION_LABELS[pt]}</b></TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {features.map((feature) => {
                            const perms = userOverrides[feature.id] || {};
                            return (
                              <TableRow key={feature.id} hover>
                                <TableCell>{feature.display_name}</TableCell>
                                {PERMISSION_TYPES.map((pt) => (
                                  <TableCell key={pt} align="center">
                                    <Checkbox
                                      checked={Boolean(perms[pt])}
                                      color="primary"
                                      size="medium"
                                      onChange={(e) => this.handleUserOverrideToggle(feature.id, pt, e.target.checked)}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </ErrorBoundary>
              </div>
            </div>
          )}

          {/* ── Tab 4: Workflow Permissions ── */}
          {activeTab === 3 && (
            <div className='tab-content'>
              <div className='cz-container'>
                <ErrorBoundary>
                  <div className="rounded-card profile-admin-table-wrapper">
                    <p style={{marginBottom: 16, color: '#7f8c8d', fontSize: 13}}>
                      Configure which roles can perform approve, reject, and rollback actions in the workflow.
                    </p>
                    <Table className='data-table'>
                      <TableHead>
                        <TableRow>
                          <TableCell><b>Role</b></TableCell>
                          <TableCell align="center"><b>Approve</b></TableCell>
                          <TableCell align="center"><b>Reject</b></TableCell>
                          <TableCell align="center"><b>Rollback</b></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRoles.map((role) => {
                          const perms = workflowPermissions.find((p) => p.role_id === role.id) || {};
                          return (
                            <TableRow key={role.id} hover>
                              <TableCell>{role.display_name || role.role_name}</TableCell>
                              {[['can_approve', 'canApprove'], ['can_reject', 'canReject'], ['can_rollback', 'canRollback']].map(([dbField, apiField]) => (
                                <TableCell key={dbField} align="center">
                                  <Switch
                                    checked={Boolean(perms[dbField])}
                                    color="primary"
                                    onChange={(e) => this.handleWorkflowPermissionToggle(role.id, dbField, e.target.checked)}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                        {allRoles.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No roles found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </ErrorBoundary>
              </div>
            </div>
          )}

          {/* ── Create / Edit Role Dialog ── */}
          <Dialog open={editRoleDialogOpen} onClose={this.handleCloseEditRole} maxWidth="sm" fullWidth>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add New Role'}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                label="Role Name"
                helperText="Internal identifier (no spaces)"
                value={editRoleName}
                onChange={(e) => this.setState({editRoleName: e.target.value})}
                fullWidth
                className="margin-bottom-16"
              />
              <TextField
                label="Display Name"
                helperText="Human-readable label"
                value={editRoleDisplayName}
                onChange={(e) => this.setState({editRoleDisplayName: e.target.value})}
                fullWidth
                className="margin-bottom-16"
              />
              <FormControl fullWidth className="margin-bottom-16">
                <InputLabel>User Group</InputLabel>
                <Select
                  value={editRoleGroupType}
                  onChange={(e) => this.setState({editRoleGroupType: e.target.value})}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button label="Cancel" className="btn btn-default" onClick={this.handleCloseEditRole} />
              <Button label="Save" className="btn btn-action" onClick={this.handleSaveEditRole} disabled={!editRoleName.trim() || !editRoleDisplayName.trim()} />
            </DialogActions>
          </Dialog>

          {/* ── Delete Role Confirmation Dialog ── */}
          <Modal
            open={deleteRoleDialogOpen}
            title="Delete Role"
            content={`Are you sure you want to delete the role "${roleToDelete ? roleToDelete.display_name : ''}"? This will remove all associated permissions.`}
            primaryButton="Delete"
            secondaryButton="Cancel"
            close={() => this.setState({deleteRoleDialogOpen: false, roleToDelete: null})}
            confirm={this.handleConfirmDeleteRole}
          />
        </div>
      </div>
    );
  }
}

export default UserProfileAdmin;
